import React, { useContext } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { AuthProvider, AuthContext } from "./context/AuthContext";
import { SurveyProvider, SurveyContext } from "./context/SurveyContext";
import { NewsfeedProvider } from "./context/NewsfeedContext";
import { SocketProvider } from "./context/SocketContext";
import ConditionInitializer from "./components/ConditionInitializer";
import InstanceLoader from "./components/InstanceLoader";
import Register from "./components/Register";
import Login from "./components/Login";
import Newsfeed from "./components/Newsfeed";
import Profile from "./components/Profile";
import PrivateRoute from "./components/PrivateRoute";
import Navbar from "./components/Navbar";
import BottomNav from "./components/BottomNav";
import MobileTopBar from "./components/MobileTopBar";
import NotificationsPage from "./components/NotificationsPage";
import AdminDashboard from "./components/AdminDashboard";
import AdminRoute from "./components/AdminRoute";
import AdminLogin from "./components/AdminLogin";
import InstanceDetail from "./components/admin/InstanceDetail";
import ConditionAnalytics from "./components/admin/ConditionAnalytics";
import InstanceAnalytics from "./components/admin/InstanceAnalytics";
import StudyAnalytics from "./components/admin/StudyAnalytics";
import Search from "./components/Search";
import api from "./services/api";
import MessageDetail from "./components/MessageDetail";
import StudyFlow from "./components/StudyFlow";
import ToastContainer from "./components/ToastContainer";
import LegalNotice from "./components/LegalNotice";
import NotificationPanel from "./components/NotificationPanel";

// Root route: redirect to active study or show fallback
const StudyRouter = () => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const redirect = async () => {
      try {
        const { data } = await api.get("/studies/active");
        // Preserve an incoming participant/tracking id (e.g. Bilendi's ?tid=)
        // so it is saved and joins the dataset. Only generate a random id when
        // the visitor arrived without one. Forwarded as `tid` (StudyFlow reads
        // tid, then pid). Test mode is carried through if present.
        const params = new URLSearchParams(window.location.search);
        const pid =
          params.get("tid") ||
          params.get("pid") ||
          "p_" + Math.random().toString(36).substring(2, 10);
        const test = params.get("test") === "1" ? "&test=1" : "";
        window.location.replace(
          `/study?s=${data.id}&tid=${encodeURIComponent(pid)}${test}`
        );
      } catch (err) {
        setError(err.response?.status === 404 ? "no_study" : "error");
        setLoading(false);
      }
    };
    redirect();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center max-w-md mx-4">
        {error === "no_study" ? (
          <>
            <h1 className="text-xl font-bold text-white mb-2">No Active Study</h1>
            <p className="text-gray-400 text-sm mb-4">
              There is currently no study running. If you received a participation link, please use that link directly.
            </p>
            <a href="/admin/login" className="text-blue-400 hover:text-blue-300 text-sm">
              Admin login
            </a>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-gray-400 text-sm">Please try again later or contact the research team.</p>
          </>
        )}
      </div>
    </div>
  );
};

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isStudyRoute = location.pathname === "/study";
  const { user } = useContext(AuthContext);
  const { instanceId, conditionId } = useContext(SurveyContext);

  const handleInteraction = async (type, targetId) => {
    if (user && user._id) {
      try {
        await api.post(`/interactions/${user._id}`, {
          type,
          targetId,
          instanceId,
          conditionId,
        });
        console.log("Interaction recorded successfully");
      } catch (error) {
        console.error("Error recording interaction:", error);
      }
    }
  };

  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = React.useState(false);
  const toggleNotificationPanel = React.useCallback(() => setIsNotificationPanelOpen(prev => !prev), []);

  // Study route renders its own full-screen UI
  if (isStudyRoute) {
    return (
      <Routes>
        <Route path="/study" element={<StudyFlow />} />
      </Routes>
    );
  }

  return (
    <div className={isAdminRoute ? "w-full" : "w-full lg:w-[1300px] mx-auto lg:flex bg-black"}>
      {!isAdminRoute && <div className="hidden lg:block"><Navbar onToggleNotifications={toggleNotificationPanel} notificationPanel={user ? <NotificationPanel isOpen={isNotificationPanelOpen} onClose={() => setIsNotificationPanelOpen(false)} desktop /> : null} /></div>}
      {!isAdminRoute && <MobileTopBar />}
      <div className={isAdminRoute ? "w-full" : "flex-1 bg-black pt-14 lg:pt-0 pb-16 lg:pb-0"}>
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route element={<PrivateRoute />}>
          <Route
            path="/profile/:username"
            element={<Profile onInteraction={handleInteraction} />}
          />
          <Route
            path="/newsfeed"
            element={<Newsfeed onInteraction={handleInteraction} />}
          />
          <Route path="/search" element={<Search />} />
          <Route path="/messages/:id" element={<MessageDetail />} />
          <Route path="/notifications" element={<NotificationsPage />} />
        </Route>
        <Route element={<AdminRoute />}>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/conditions/:conditionId/analytics" element={<ConditionAnalytics />} />
          <Route path="/admin/instances/:instanceId" element={<InstanceDetail />} />
          <Route path="/admin/instances/:instanceId/analytics" element={<InstanceAnalytics />} />
          <Route path="/admin/studies/:studyId/analytics" element={<StudyAnalytics />} />
        </Route>
        <Route path="/legal" element={<LegalNotice />} />
        <Route path="/" element={<StudyRouter />} />
        <Route path="*" element={
          <div className="min-h-screen bg-black flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-400 mb-2">404</h1>
              <p className="text-gray-500 mb-4">Page not found</p>
              <a href="/" className="text-spiders-primary-400 hover:underline">Return to home</a>
            </div>
          </div>
        } />
      </Routes>
      </div>
      {!isAdminRoute && <BottomNav onToggleNotifications={toggleNotificationPanel} />}
      {!isAdminRoute && user && <ToastContainer />}
      {!isAdminRoute && user && (
        <NotificationPanel
          isOpen={isNotificationPanelOpen}
          onClose={() => setIsNotificationPanelOpen(false)}
          mobile
        />
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <SurveyProvider>
          <SocketProvider>
            <NewsfeedProvider>
              <ConditionInitializer>
                <InstanceLoader>
                  <AppContent />
                </InstanceLoader>
              </ConditionInitializer>
            </NewsfeedProvider>
          </SocketProvider>
        </SurveyProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
